/**
 * How a plan's numbers are written, in one place.
 *
 * These lived in `Planner.tsx` until a saved trip could be reopened, at which point two screens rendered
 * the same itinerary and a second copy of "chưa xác định" would have been a second answer to the same
 * question. The unit conversions are the reason this is a module rather than a component: every one of
 * them has a rule behind it (§18 for the nulls, and the two directions of "distance" and "time" that
 * the API reports in different units).
 */

import type { PlanStop } from "@trippool/contracts";

import { copy } from "../copy.ts";
import { displayName } from "./placeName.ts";

const UNKNOWN = copy.state.unknown;

/**
 * §18: a distance the routing provider did not return is never shown, so a `null` metric renders as
 * "chưa xác định" rather than an estimate — and never as `0`, which would state that the two stops
 * are the same place, a different and false claim.
 */
export function formatKm(metres: number | null): string {
  return metres === null ? UNKNOWN : `${(metres / 1000).toFixed(1)} km`;
}

/** Driving time arrives in seconds from the planner and is shown in minutes. */
export function formatDriving(seconds: number | null): string {
  return seconds === null ? UNKNOWN : `${Math.round(seconds / 60)} phút`;
}

/**
 * The same, for a value the API already reports in minutes.
 *
 * A separate function rather than `formatDriving(minutes * 60)`, which is what the day heading did
 * first: multiplying by 60 to divide by 60 again is the kind of round trip that survives review and
 * then breaks when one of the two units changes.
 */
export function formatDrivingMinutes(minutes: number | null): string {
  return minutes === null ? UNKNOWN : `${minutes} phút`;
}

/** Rest time arrives in seconds and is shown in hours to one decimal. */
export function formatRestHours(seconds: number | null): string {
  return seconds === null ? UNKNOWN : `${(seconds / 3600).toFixed(1)}h`;
}

/**
 * Whether every leg of a day has a measured distance.
 *
 * Derived rather than read from a flag, because `PlanVariantDetail` does not carry one and a second
 * request for `GET .../days/:dayIndex/route` would be a round trip for a boolean. The rule is the same
 * one the endpoint applies server-side: one unmeasured leg makes the day's total unknown, and a day
 * with no legs at all is trivially known — nothing was driven.
 */
export function dayDistancesKnown(day: { legs: Array<{ distance_m: number | null }> }): boolean {
  return day.legs.every((leg) => leg.distance_m !== null);
}

/** The plan's derived items (kinds other than a place visit) and how a roadmap line shows them. */
export function stopPresentation(kind: Exclude<PlanStop["kind"], "visit">): {
  icon: string;
  label: string;
} {
  switch (kind) {
    case "travel":
      return { icon: "🚗", label: "Di chuyển" };
    case "rest":
      return { icon: "☕", label: "Nghỉ" };
    case "meal":
      return { icon: "🍜", label: "Ăn" };
    case "lodging":
      return { icon: "🏨", label: "Nghỉ đêm" };
    // Unreachable while `kind` is the contract's closed enum; typed as itself so a value the
    // contract later adds degrades to its own word instead of a blank.
    default:
      return { icon: "•", label: kind };
  }
}

/**
 * What a stop is called on a line of text.
 *
 * Three kinds are anchored to a real place with a name — `visit`, `lodging`, `entry` — and for those the
 * name is the label, because "Nghỉ đêm" does not say which hotel and the whole point of the feature is
 * which hotel. The derived kinds, a meal or a rest, have no name of their own and use their kind's label.
 *
 * One rule, because two renderers draw the same itinerary: the roadmap's timeline and the option card's
 * chain, and a stop named one way in one of them and another way in the other is the kind of difference
 * nobody reports and everybody half-notices.
 *
 * **The name is formatted here rather than printed as stored**, which is where a plan's stop names enter
 * the screens: `displayName` is the arrange step's own rule for reading a name (`placeName.ts`), and the
 * itinerary is a second surface showing the same places. Measured in one session, the store read
 * `Chuyencuanang 5` while the generated chain and the timeline read `chuyencuanang5` — the same place,
 * named two ways, and the one the user typed the name into was the one that looked wrong. One call here
 * covers the option card's chain, the roadmap's timeline, its map pins (`dayPins`) and the trip page,
 * because all four read a stop through this function or that one. The call is safe to repeat:
 * `displayName` touches only the first character and the letter-digit boundary, so a name already
 * formatted comes back exactly as it went in.
 */
export function stopLabel(stop: PlanStop): string {
  switch (stop.kind) {
    case "visit":
      return displayName(stop.name);
    case "lodging":
      return `🏨 ${displayName(stop.name)}`;
    case "entry":
      return `🚉 ${displayName(stop.name)}`;
    /*
     * A rest names where it happens.
     *
     * The party drives back to the room, so "Nghỉ" alone leaves out the one thing the row is for — a rest
     * taken at the lodging reads as "Nghỉ ở Nhà Sói Homstay", and the same stop kind would carry a place's
     * name wherever the planner later puts one.
     */
    case "rest":
      return `☕ Nghỉ ở ${displayName(stop.name)}`;
    default: {
      const presentation = stopPresentation(stop.kind);
      return `${presentation.icon} ${presentation.label}`;
    }
  }
}

/**
 * Minutes from midnight as a clock time.
 *
 * `PlanStop.start_min` is minutes from midnight in the trip's own timezone — the API converts a stored
 * instant with the Vietnam offset before sending it. Null is not midnight: it means no schedule could be
 * derived, which reads as "chưa xác định" like every other unmeasured value here.
 */
export function formatClock(minutes: number | null): string {
  if (minutes === null) return copy.state.unknown;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
}

/**
 * A day's stops as map pins.
 *
 * Numbered from the stop's own `position`, not from its index in this array, so a pin matches the row
 * beside it rather than being renumbered from whatever subset could be placed on the map. A stop with no
 * location is dropped here and nowhere else — the plan still lists it, and the caller's own text says
 * which constraints could not be satisfied.
 *
 * The returned shape is structural rather than an import of the map's own type: this module is about
 * plans, and a plan does not know what draws it.
 *
 * The name is `displayName`'d for the same reason `stopLabel`'s is: the pin's popup and the row beside it
 * are one place, and the route map prints this string in both.
 */
export function dayPins(day: { stops: PlanStop[] }): Array<{
  id: string;
  name: string;
  lat: number;
  lon: number;
  position: number;
  /** What the pin shows: a visit's number, or an anchor's glyph. */
  marker: string;
  timeLabel?: string | null;
}> {
  const markers = stopMarkers(day.stops);
  return day.stops.flatMap((stop, index) =>
    stop.location === null
      ? []
      : [
          {
            id: `${stop.place_id ?? stop.position}-${index}`,
            name: displayName(stop.name),
            lat: stop.location.lat,
            lon: stop.location.lon,
            // The key, not the label: the highlight is keyed on this and it never changes meaning.
            position: stop.position + 1,
            marker: markers[index] ?? String(stop.position + 1),
            timeLabel: stop.start_min !== null ? formatClock(stop.start_min) : null,
          },
        ],
  );
}

/**
 * What each stop shows on the map and beside it, one entry per stop, in the day's own order.
 *
 * **Visits are numbered 1..n over the visits alone.** Numbering every stop left gaps — a day read "1, 3"
 * because the rest between them was the second stop — and a list that skips a number reads as a bug rather
 * than as a deliberate omission. Anchors carry a glyph instead, because they are not in the order at all:
 * they are where the day starts, ends, or returns to rest.
 *
 * One function for both surfaces. The whole point of the marker is that the pin and the row agree, and two
 * implementations of "which number is this" would drift the first time either changed.
 */
export function stopMarkers(stops: readonly { kind: PlanStop["kind"] }[]): string[] {
  let visits = 0;
  return stops.map((stop) => {
    if (stop.kind !== "visit") return stopIcon(stop.kind);
    visits += 1;
    return String(visits);
  });
}

/**
 * The glyph a pin and a timeline row use for a stop that is not a visit.
 *
 * One function for both, because the whole point of the icon is that the pin and the row agree — two mappings
 * would drift and the correspondence is the reason the number exists at all.
 */
export function stopIcon(kind: PlanStop["kind"]): string {
  switch (kind) {
    case "lodging":
      return "🏨";
    case "entry":
      return "🚉";
    case "rest":
      return "☕";
    default:
      return "📍";
  }
}
